/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- guide payload and pcg-core are loose-typed by design */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-argument -- pcg-core surface is loose-typed by design */

// JSX rewrite of the prototype's render function: the "Provisional Results" pattern-results bar
// (renderPatternResults, handoff v0.8.3) as JSX, plus the
// resultItem/energyMetricItem HTML string builders.
//
// Rewrite discipline (same as confidence-card.tsx): every label/class/hover
// branch is copied verbatim — it IS display contract. Template strings became
// JSX; escapeHtml disappears (React escapes).
//
// Handoff v0.8.3 qualification model: "N/A" became "Unassessable △"; values
// that are reported but qualified (cross-position count review/correction,
// Limited timing/variation support, provisional phase timing, lower-confidence
// Energy) get a trailing " △" and the qualification sentence replaces the
// default hover. resultItem/energyMetricItem turn every "△" in the value string
// into a `.result-qualification-glyph` span — upstream does it with
// `String(value).replace(/\s*△/g, '<span …>△</span>')`; QualifiedValue below
// splits the string and renders the span (no innerHTML).
//
// Handoff v0.8.3.10 compact warning states: an "Unassessable" Timing value
// and a "Limited" / "Unassessable" Energy cell collapse to a single amber ⚠
// glyph (role="img", the state as its accessible name) and the state plus its
// explanation move into the hover, so the longest supported state can never
// displace an adjacent field. Timing qualification glyphs render as ⚠ too;
// Energy keeps △ for its qualified-but-reported values, as upstream.
//
// Prototype v0.8.3.18 (pcg-viewer-mvp git 6b385e4) provisional Energy: a
// recording whose usable Timing run is shorter than the automated Energy
// threshold reads "Limited" in all four Energy cells, with a hover saying the
// estimate is provisional and needs manual review — whether or not the murmur
// band carries substantial energy (before, a recording without it read
// "Unassessable"). The branch that would show the provisional SYS / DIA
// strength and cycle percentages (`showProvisionalEnergy`) is ported verbatim
// but needs scored activity, and activity is still only scored from the full
// cycle threshold up (acousticActivityForCycles), upstream as here — so no
// provisional number is displayed yet.
//
// Exam-overlay review cues (the handoff's `applyExamResultCues`, run by the
// exam layer over the rendered bar) are applied here at render time instead:
// see examResultCue / withExamCue below.
//
// Timing engine (upstream Medaica Viewer v0.9.1, m1acq): for a guide whose
// engine selected an Evaluation Range (`core.ctx.evaluationRange`) the core is
// already the range's metric scope; on top of it this card applies what
// upstream's page shows once the engine has re-rendered the results — the v0.9
// presentation rules (no BPM / Rate qualification, the relaxed phase-limited
// Energy state and lower-confidence Energy qualification) and
// `applyEvalRangeResultValues` (BPM is the range's engine rate, Rate is
// classified from it). When the engine block is present but not ready
// (`core.ctx.engine.status === "unavailable"`) every value reads "Unavailable"
// without a hover, as upstream's "Analysis unavailable" state does.
//
// DISPLAY CONTRACT (enforced here): timing numbers are gated on
// analysisQuality() (`status` / `summaryAllowed`), NEVER on timingStats()
// returning "-" placeholders — timingStats() keeps emitting real numbers
// (hr 111, n 36 on emory-six-m1) after the client declares the recording
// Unreliable. The explicit `q.status !== "unreliable"` term below is a
// deliberate belt-and-braces addition to the prototype's expression (it is
// behavior-identical on the corpus: unreliable ⇒ summaryAllowed false).

import React, { Fragment, ReactElement, ReactNode } from "react"
import { fmtNumber, fmtMs } from "../core/format"
import { rejectGate } from "../core/gate"
import { confidenceTier, signalQualityTier, soiTrackConfidence } from "../core/classifiers"
import { ENGINE_RATIO_CALCULATION, ratePatternFromHr } from "../core/timing"
import { energyStrengthTier } from "../core/energy"
import { phaseAlternateActivity } from "../core/phase"
import { MIN_ENERGY_RELIABLE_CYCLES } from "../core/constants"
import { ENGINE_UNAVAILABLE } from "../core/evaluation-range"

type Tier = { label: string; className?: string }
type Pattern = Tier & { hover?: string }
type Cell = { label: string; value: string; className?: string; hover?: string }

// Port of upstream `String(value).replace(/\s*△/g, '<span class="result-qualification-glyph" aria-hidden="true">GLYPH</span>')`:
// the glyph (and any whitespace before it) becomes a span, the rest is text.
// The rendered glyph is ⚠ in the Timing block and △ in the Energy block.
const QualifiedValue = ({ value, glyph = "△" }: { value: string; glyph?: string }): ReactElement => {
  const parts = String(value).split(/\s*△/g)
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {part}
          {index < parts.length - 1 ? (
            <span className="result-qualification-glyph" aria-hidden="true">
              {glyph}
            </span>
          ) : null}
        </Fragment>
      ))}
    </>
  )
}

// Exam-overlay review cues (handoff `applyExamResultCues`): values outside the
// expected range are cued red, borderline values amber — a class on the value
// and a one-sentence addition to its hover text; nothing is drawn on the
// value itself. Matching is on the rendered text, exactly as the overlay
// post-processed the DOM; unavailable values and the HR number itself are
// never cued.
const RED_CUE_WORDS = ["elevated", "high", "prominent", "detected", "rejected"]
const AMBER_CUE_WORDS = ["moderate", "borderline", "periodic", "low", "fair", "limited"]
const RED_CUE_TEXT = "Review cue: this result is outside the expected range for this recording."
const AMBER_CUE_TEXT = "Review cue: this result is borderline or warrants attention for this recording."
export function examResultCue(label: string, value: string, className = ""): "red" | "amber" | "" {
  if (className.split(/\s+/).includes("unavailable")) return ""
  const text = String(value).trim().toLowerCase()
  if (!text || ["normal", "good", "n/a", "-"].includes(text)) return ""
  let cue: "red" | "amber" | "" = ""
  if (RED_CUE_WORDS.some((word) => text.includes(word))) cue = "red"
  else if (AMBER_CUE_WORDS.some((word) => text.includes(word))) cue = "amber"
  if (label.toLowerCase().includes("hr") && text.endsWith("bpm")) cue = ""
  return cue
}
// The cue matches on rendered text, exactly as the upstream overlay
// post-processed the DOM — a compact ⚠ cell has no words to match.
const NO_CUE_TEXT = "⚠"
const withExamCue = (
  label: string,
  value: string,
  className: string,
  hover: string
): { className: string; hover: string } => {
  const cue = examResultCue(label, value, className)
  if (!cue) return { className, hover }
  const cueText = cue === "red" ? RED_CUE_TEXT : AMBER_CUE_TEXT
  return {
    className: `${className} exam-result-cue-${cue}`.trim(),
    hover: hover ? `${hover} ${cueText}` : cueText,
  }
}

const ResultItem = ({
  label,
  value,
  className: baseClassName = "",
  hover: baseHover = "",
}: {
  label: string
  value: string
  className?: string
  hover?: string
}): ReactElement => {
  const compactUnassessable = String(value).startsWith("Unassessable")
  const effectiveHover = compactUnassessable
    ? `Unassessable — ${baseHover || "this result could not be calculated reliably."}`
    : baseHover
  const { className, hover } = withExamCue(
    label,
    compactUnassessable ? NO_CUE_TEXT : value,
    baseClassName,
    effectiveHover
  )
  return (
    <div className={`result-item${hover ? " hover-help" : ""}`} {...(hover ? { "data-hover": hover } : {})}>
      <div className="result-label">{label}:</div>
      <div className={`result-value ${className}`}>
        {compactUnassessable ? (
          <span className="result-qualification-glyph" role="img" aria-label="Unassessable">
            ⚠
          </span>
        ) : (
          <QualifiedValue value={value} glyph="⚠" />
        )}
      </div>
    </div>
  )
}

const EnergyMetricItem = ({
  label,
  value,
  className: baseClassName = "",
  hover: baseHover = "",
}: {
  label: string
  value: string
  className?: string
  hover?: string
}): ReactElement => {
  const energyState = String(value).replace(/\s*△/g, "").trim()
  const compactQualification = energyState === "Limited" || energyState === "Unassessable"
  const effectiveHover = compactQualification
    ? `${energyState} — ${
        energyState === "Limited" ? "result confidence is limited" : "cycle percentage could not be calculated reliably"
      }. ${baseHover || ""}`.trim()
    : baseHover
  const { className, hover } = withExamCue(
    label,
    compactQualification ? NO_CUE_TEXT : value,
    baseClassName,
    effectiveHover
  )
  const limitMark: ReactNode = className.split(/\s+/).includes("phase-limited") ? (
    <sup className="energy-limit-mark" aria-hidden="true">
      *
    </sup>
  ) : null
  return (
    <div className={`energy-metric${hover ? " hover-help" : ""}`} {...(hover ? { "data-hover": hover } : {})}>
      <div className="energy-metric-label">{label}</div>
      <div className={className ? `energy-metric-value ${className}` : "energy-metric-value"}>
        {compactQualification ? (
          <span className="energy-warning-glyph" role="img" aria-label={energyState}>
            ⚠
          </span>
        ) : (
          <QualifiedValue value={value} />
        )}
        {limitMark}
      </div>
    </div>
  )
}

const PcgPatternResultsCard = ({ core, recordingLabel }: { core: any; recordingLabel?: string }): ReactElement => {
  const D = core.ctx.payload
  const phaseReviewResolution = core.ctx.phaseReviewResolution
  // The v0.9 presentation rules apply once the timing engine has selected an
  // Evaluation Range for this guide (upstream: the `pcg-exam-v0900` re-render).
  const evalRange = core.ctx.evaluationRange || null
  const isV09EvalUi = Boolean(evalRange)
  const engineUnavailable = core.ctx.engine?.status === ENGINE_UNAVAILABLE
  const q = core.analysisQuality()
  const gate = rejectGate(D)
  const baseActivity = core.visualAcousticActivityStats()
  const activity = phaseReviewResolution === "alternate" ? phaseAlternateActivity(baseActivity) : baseActivity
  const tracks = soiTrackConfidence(D, activity, q)
  const signalTier: Tier = signalQualityTier(tracks.signalScore, gate)
  const timingTier: Tier = confidenceTier(tracks.timingScore)
  const patternTier: Tier = confidenceTier(tracks.patternScore)
  const timing = core.timingConsistency()
  // Display contract: gate on analysis-quality status, never on "-" placeholders.
  const allowCycleTiming = q.status !== "unreliable" && signalTier.label !== "Unreliable" && q.summaryAllowed
  const allowVariation = allowCycleTiming && Number.isFinite(Number(timing.timingSwingPct))
  const phaseConfirmed = phaseReviewResolution === "current" || phaseReviewResolution === "alternate"
  const allowPhaseTiming = allowCycleTiming && (timing.label !== "Limited" || phaseConfirmed)
  const timingSupportsPhaseEnergy = allowPhaseTiming && timingTier.label !== "Unreliable"
  const allowEnergyFindings =
    activity &&
    signalTier.label !== "Unreliable" &&
    ((timingSupportsPhaseEnergy && q.visualEnergyAllowed) || phaseConfirmed)
  const baseTimingStats = core.timingStats()
  const s =
    phaseReviewResolution === "alternate" && baseTimingStats.sys !== "-" && baseTimingStats.dia !== "-"
      ? { ...baseTimingStats, sys: baseTimingStats.dia, dia: baseTimingStats.sys }
      : baseTimingStats
  // The BPM qualification (handoff v0.8.3): the cross-position count-correction
  // / review sentence, else the Limited-timing sentence. Upstream v0.9 clears
  // it under the `pcg-exam-v0900` body class, but that class is added only
  // after the results have rendered, and only the timing engine re-renders
  // them — so the qualification stays unless the engine selected a range.
  const timingCorrection = core.crossPositionTimingCorrection()
  const timingReview = core.crossPositionTimingReview()
  const countQualification = timingCorrection
    ? `Likely half/double count corrected to ${timingCorrection.correctedBpm} bpm from the reliable exam-position consensus. The applied interpretation and alternatives remain in the audit data.`
    : timingReview
    ? `This recording's rate differs from the reliable exam-position consensus and may reflect a half/double count or uncertain S1/S2 localization.`
    : ""
  const timingSupportQualification =
    timingTier.label === "Limited"
      ? "Timing is provisional because fewer or lower-confidence S1-to-S1 features support this recording."
      : ""
  const hrQualification = isV09EvalUi ? "" : countQualification || timingSupportQualification
  const variationQualification =
    patternTier.label === "Limited" ? "Variation is provisional because cycle-to-cycle timing support is limited." : ""
  const phaseTimingQualification =
    !phaseConfirmed && timing.label === "Limited"
      ? "Systole-to-diastole phase timing is provisional because S1/S2 phase assignment is limited."
      : ""
  // Handoff v0.8.3.8: BPM is the field heading, so the value is the bare number
  const hrValueBase = allowCycleTiming && Number.isFinite(Number(s.hr)) ? `${fmtNumber(s.hr, 0)}` : "Unassessable △"
  const hrValue = hrQualification && !hrValueBase.startsWith("Unassessable") ? `${hrValueBase} △` : hrValueBase
  const ratePatternBase: Pattern = allowCycleTiming
    ? ratePatternFromHr(s.hr)
    : {
        label: "Unassessable △",
        className: "unavailable",
        hover:
          "Fewer than the required reliable S1-to-S1 features were available for a defensible rate classification.",
      }
  const ratePattern: Pattern =
    hrQualification && !ratePatternBase.label.startsWith("Unassessable")
      ? { ...ratePatternBase, label: `${ratePatternBase.label} △` }
      : ratePatternBase
  const rhythmPatternBase: Pattern = allowVariation
    ? core.rhythmPatternFromTiming(timing)
    : {
        label: "Unassessable △",
        className: "unavailable",
        hover: "Fewer than the required reliable S1-to-S1 features were available for a defensible variation estimate.",
      }
  const rhythmPattern: Pattern =
    variationQualification && !rhythmPatternBase.label.startsWith("Unassessable")
      ? { ...rhythmPatternBase, label: `${rhythmPatternBase.label} △` }
      : rhythmPatternBase
  const regularValueBase = allowVariation ? `±${fmtNumber(timing.timingSwingPct, 1)}%` : "Unassessable △"
  const regularValue =
    variationQualification && !regularValueBase.startsWith("Unassessable") ? `${regularValueBase} △` : regularValueBase
  const timingVarHover = allowVariation
    ? `Approximate S1-to-S1 timing variation is ±${fmtNumber(timing.timingSwingPct, 1)}%${
        Number.isFinite(Number(timing.timingSwingMs)) && Number.isFinite(Number(timing.medianCycleMs))
          ? `, about ±${fmtNumber(timing.timingSwingMs, 0)} ms around a ${fmtNumber(
              timing.medianCycleMs,
              0
            )} ms median cycle`
          : ""
      }.${
        timing.label === "Limited" ? " Lower-confidence provisional timing evidence for clinician review." : ""
      } PCG timing estimate; confirm with clinical review when needed.`
    : "Timing variability was not assessed because there are not enough usable S1-to-S1 cycles."
  const sysMs = allowPhaseTiming ? s.sys : "-"
  const diaMs = allowPhaseTiming ? s.dia : "-"
  const sysDiaDurations = allowPhaseTiming ? `${fmtMs(sysMs)}ms / ${fmtMs(diaMs)}ms` : "Unassessable"
  // The timing engine reports median durations; the cycle calculation, means
  const sysDiaStatistic = s.ratioCalculation === ENGINE_RATIO_CALCULATION ? "Median" : "Mean"
  const ratio = allowPhaseTiming && Number(diaMs) > 0 ? Number(sysMs) / Number(diaMs) : null
  const ratioValueBase =
    Number.isFinite(ratio as number) && (ratio as number) > 0
      ? `1:${(1 / (ratio as number)).toFixed(1)}`
      : "Unassessable △"
  const ratioValue =
    phaseTimingQualification && !ratioValueBase.startsWith("Unassessable") ? `${ratioValueBase} △` : ratioValueBase
  const showProvisionalEnergy = !allowEnergyFindings && q.visualEnergyProvisional && activity
  const showEnergyValues = allowEnergyFindings || showProvisionalEnergy
  const showLimitedEnergy = !allowEnergyFindings && q.visualEnergyLimited
  const sysEnergy: Tier = showEnergyValues
    ? { ...energyStrengthTier(activity.sysMean) }
    : showLimitedEnergy
    ? { label: "Limited △", className: "limited" }
    : { label: "Unassessable △", className: "unavailable" }
  const diaEnergy: Tier = showEnergyValues
    ? { ...energyStrengthTier(activity.diaMean) }
    : showLimitedEnergy
    ? { label: "Limited △", className: "limited" }
    : { label: "Unassessable △", className: "unavailable" }
  let sysCycleText = showEnergyValues
    ? `${fmtNumber(activity.sysCyclePct, 0)}%`
    : showLimitedEnergy
    ? "Limited △"
    : "Unassessable △"
  let diaCycleText = showEnergyValues
    ? `${fmtNumber(activity.diaCyclePct, 0)}%`
    : showLimitedEnergy
    ? "Limited △"
    : "Unassessable △"
  if (showProvisionalEnergy) {
    if (!sysEnergy.label.includes("△")) sysEnergy.label += " △"
    if (!diaEnergy.label.includes("△")) diaEnergy.label += " △"
    if (!sysCycleText.includes("△")) sysCycleText += " △"
    if (!diaCycleText.includes("△")) diaCycleText += " △"
    sysEnergy.className = `${sysEnergy.className || ""} limited`.trim()
    diaEnergy.className = `${diaEnergy.className || ""} limited`.trim()
  }
  // Phase-limited Energy (Medaica Viewer v0.9.1): only an atypical S1/S2
  // morphology limits the phase assignment — no longer every recording whose
  // phase review was needed. Upstream v0.9 further relaxes this state and the
  // lower-confidence Energy qualification under the `pcg-exam-v0900` body
  // class; as with the BPM qualification above, that takes effect only for a
  // guide with an Evaluation Range.
  const phaseLimited =
    !phaseConfirmed && q.visualEnergyPhaseLimited && !q.visualEnergyProvisional && !(isV09EvalUi && allowEnergyFindings)
  const v09EnergyConfidenceAligned =
    isV09EvalUi && q.visualEnergyAllowed && Number(q.selectedTimingCycleCount || 0) >= MIN_ENERGY_RELIABLE_CYCLES
  const lowConfidenceEnergy =
    !v09EnergyConfidenceAligned &&
    allowEnergyFindings &&
    confidenceTier(Number(q.visualEnergyMarkerConfidence || q.energyMarkerConfidence || q.markerConfidence || 0))
      .label === "Limited"
  const energyQualification = lowConfidenceEnergy
    ? "Acoustic activity is displayed, but the supporting Energy evidence is lower confidence."
    : ""
  const phaseLimitMessage = showProvisionalEnergy
    ? `Limited evidence — manual review required. Provisional estimate based on ${
        q.selectedTimingCycleCount || 0
      } usable cycles; the automated acceptance threshold is ${
        q.phaseEnergyMinSelectedCycles || MIN_ENERGY_RELIABLE_CYCLES
      }. Listen and confirm or reject this result.`
    : showLimitedEnergy
    ? q.visualEnergyReason ||
      `Moderate-or-higher murmur-band Energy is visible, but there are only ${
        q.selectedTimingCycleCount || 0
      } selected Timing cycles, below the minimum of ${
        q.phaseEnergyMinSelectedCycles || MIN_ENERGY_RELIABLE_CYCLES
      } required for SYS/DIA phase scoring.`
    : q.visualEnergyPhaseLimited
    ? "Energy activity is visible, but SYS/DIA phase assignment is limited because S1/S2 guide morphology is atypical."
    : `Heart rate and broad timing remain available, but SYS/DIA Energy needs stricter, stable S1/S2 phase alignment. ${
        q.energyReason || "The recording did not meet the stricter phase-assignment confidence requirement."
      }`
  const phaseLimitText = phaseLimited ? ` ${phaseLimitMessage}` : ""
  const sysHover = showEnergyValues
    ? `Systolic energy strength is ${sysEnergy.label}. ${sysCycleText} of scored systolic cycles were over the repeatable activity threshold.${phaseLimitText}`
    : showLimitedEnergy
    ? `${phaseLimitMessage} Systolic strength and cycle percentage were not assigned.`
    : `Systolic energy was not assessed. ${
        q.visualEnergyReason || q.energyReason || "Not enough selected Timing cycles for Energy display."
      }`
  const diaHover = showEnergyValues
    ? `Diastolic energy strength is ${diaEnergy.label}. ${diaCycleText} of scored diastolic cycles were over the repeatable activity threshold.${phaseLimitText}`
    : showLimitedEnergy
    ? `${phaseLimitMessage} Diastolic strength and cycle percentage were not assigned.`
    : `Diastolic energy was not assessed. ${
        q.visualEnergyReason || q.energyReason || "Not enough selected Timing cycles for Energy display."
      }`
  // Upstream appends the glyph in place after the hovers are built, so the
  // hovers quote the unqualified strength labels and cycle percentages.
  if (energyQualification && !sysEnergy.label.includes("△")) sysEnergy.label += " △"
  if (energyQualification && !diaEnergy.label.includes("△")) diaEnergy.label += " △"
  if (energyQualification && !sysCycleText.includes("△")) sysCycleText += " △"
  if (energyQualification && !diaCycleText.includes("△")) diaCycleText += " △"
  const sysEnergyClass = `${sysEnergy.className || ""}${
    phaseLimited && sysEnergy.label !== "N/A" ? " phase-limited" : ""
  }${lowConfidenceEnergy ? " low-confidence-result" : ""}`.trim()
  const diaEnergyClass = `${diaEnergy.className || ""}${
    phaseLimited && diaEnergy.label !== "N/A" ? " phase-limited" : ""
  }${lowConfidenceEnergy ? " low-confidence-result" : ""}`.trim()
  const sysCycleClass = `${sysCycleText.startsWith("Unassessable") ? "unavailable" : ""}${
    lowConfidenceEnergy ? " low-confidence-result" : ""
  }`.trim()
  const diaCycleClass = `${diaCycleText.startsWith("Unassessable") ? "unavailable" : ""}${
    lowConfidenceEnergy ? " low-confidence-result" : ""
  }`.trim()

  // applyEvalRangeResultValues (upstream): with an Evaluation Range timingStats()
  // carries the range's engine rate; the BPM cell shows it without a hover (its
  // value class stays), and Rate is classified from the unrounded rate.
  const evalRangeBpm = Number(s.evalRangeBpm)
  const hasEvalRangeBpm = Number.isFinite(evalRangeBpm)
  const hrClassName = hrValue.startsWith("Unassessable") ? "unavailable" : ""
  const bpmCell: Cell = hasEvalRangeBpm
    ? { label: "BPM", value: String(s.hr), className: hrClassName, hover: "" }
    : {
        label: "BPM",
        value: hrValue,
        className: hrClassName,
        hover: hrQualification || "Average heart rate from S1-to-S1 timing cycles.",
      }
  const evalRangeRate: Pattern | null = hasEvalRangeBpm ? ratePatternFromHr(evalRangeBpm) : null
  const rateCell: Cell = evalRangeRate
    ? { label: "Rate", value: evalRangeRate.label, className: evalRangeRate.className, hover: evalRangeRate.hover }
    : {
        label: "Rate",
        value: ratePattern.label,
        className: ratePattern.className,
        hover: hrQualification || ratePattern.hover,
      }
  // "Analysis unavailable" (upstream, timing engine not ready): every value
  // reads "Unavailable" and no cell keeps a hover.
  const cell = (props: Cell): Cell =>
    engineUnavailable ? { label: props.label, value: "Unavailable", className: "unavailable", hover: "" } : props

  // The title carries two tips, as upstream's exam layer sets them: the styled
  // hover (with the Energy caveat) and the browser tooltip (without it).
  const selectedRecordingTip = recordingLabel ? `Selected recording: ${recordingLabel}. ` : ""
  const resultsScopeTip = `${selectedRecordingTip}Detailed Timing and Energy results apply to this recording only.`

  return (
    <div className="results-bar">
      <div
        className="results-title hover-help"
        data-hover={`${resultsScopeTip} Energy is not a clinical murmur grade.`}
        title={resultsScopeTip}
      >
        Provisional Results
      </div>
      <div className="result-block timing">
        <div className="result-block-title">Timing</div>
        <ResultItem {...cell(bpmCell)} />
        <ResultItem {...cell(rateCell)} />
        <ResultItem
          {...cell({
            label: "Variation",
            value: rhythmPattern.label,
            className: rhythmPattern.className,
            hover: variationQualification || rhythmPattern.hover,
          })}
        />
        <ResultItem
          {...cell({
            label: "Timing variability",
            value: regularValue,
            className: regularValue.startsWith("Unassessable") ? "unavailable" : "",
            hover: variationQualification || timingVarHover,
          })}
        />
        <ResultItem
          {...cell({
            label: "Sys:Dia ratio",
            value: ratioValue,
            className: ratioValue.startsWith("Unassessable") ? "unavailable" : "",
            hover:
              phaseTimingQualification ||
              `Systole-to-diastole timing relationship from usable cycles. ${sysDiaStatistic} Sys/Dia: ${sysDiaDurations}.`,
          })}
        />
      </div>
      <div className="result-block energy">
        {phaseLimited ? (
          <div className="result-block-title hover-help" data-hover={phaseLimitMessage}>
            Energy
          </div>
        ) : (
          <div className="result-block-title">Energy</div>
        )}
        {/* A phase-limited recording shows no SYS / DIA values: the strength
            cells read "Limited" and the cycle cells "Unassessable" (each a ⚠),
            with the phase-limit message as their hover. */}
        {phaseLimited ? (
          <>
            <EnergyMetricItem
              {...cell({ label: "SYS", value: "Limited △", className: "limited", hover: phaseLimitMessage })}
            />
            <EnergyMetricItem
              {...cell({
                label: "Cycles",
                value: "Unassessable △",
                className: "unavailable",
                hover: phaseLimitMessage,
              })}
            />
            <EnergyMetricItem
              {...cell({ label: "DIA", value: "Limited △", className: "limited", hover: phaseLimitMessage })}
            />
            <EnergyMetricItem
              {...cell({
                label: "Cycles",
                value: "Unassessable △",
                className: "unavailable",
                hover: phaseLimitMessage,
              })}
            />
          </>
        ) : (
          <>
            <EnergyMetricItem
              {...cell({
                label: "SYS",
                value: sysEnergy.label,
                className: sysEnergyClass,
                hover: energyQualification || sysHover,
              })}
            />
            <EnergyMetricItem
              {...cell({
                label: "Cycles",
                value: sysCycleText,
                className: sysCycleClass,
                hover: energyQualification || sysHover,
              })}
            />
            <EnergyMetricItem
              {...cell({
                label: "DIA",
                value: diaEnergy.label,
                className: diaEnergyClass,
                hover: energyQualification || diaHover,
              })}
            />
            <EnergyMetricItem
              {...cell({
                label: "Cycles",
                value: diaCycleText,
                className: diaCycleClass,
                hover: energyQualification || diaHover,
              })}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default PcgPatternResultsCard
